import { useEffect, useState } from "react";
import { Control, FieldValues, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CounterInput, InputError } from "@monorepo/components";
import { findErrorByParam } from "@monorepo/helpers";
import { DeleteIcon } from "@monorepo/icons";
import theme from "@monorepo/styles/theme";
import { IResponseError, IServiceParameter, ParameterTypeEnum, ServiceMeterTypeEnum } from "@monorepo/types";
import { AddOutlined } from "@mui/icons-material";
import { Fade, Grow, IconButton, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button/Button";

type TCounterParametersInputFieldsProps<T extends FieldValues> = {
  parameters: Array<IServiceParameter>;
  inputErrors: Array<IResponseError>;
  onCalculate?: () => void;
  isThirdPartyPE?: boolean;
  control: Control<T>;
  calculateError?: string;
};

export function CounterParametersInputFields<T extends FieldValues>({
  parameters,
  inputErrors,
  control,
}: TCounterParametersInputFieldsProps<T>) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const [counters, setCounters] = useState<Array<IServiceParameter>>(parameters);

  useEffect(() => {
    setCounters(parameters);
  }, [parameters]);

  const { register } = useFormContext();

  if (!parameters.length) {
    return null;
  }
  return (
    <>
      {!!counters?.length &&
        counters.map((counter, index) => {
          const parameterName = counter.parameter_name;
          // early returning all hidden counter inputs
          if (counter.group_label && counter.hidden) {
            return (
              <input
                key={parameterName}
                {...register(counter.parameter_name)}
                hidden
                defaultValue={counter?.value}
                name={counter.parameter_name}
              />
            );
          }
          if (
            counter.group_label &&
            counter.group_label === counters[index + 1]?.group_label &&
            counter.meter_type === ServiceMeterTypeEnum.FROM
          ) {
            const secondParameter = counters[index + 1];
            // eslint-disable-next-line max-len
            const rangeError =
              findErrorByParam({ param: secondParameter.parameter_name, error: inputErrors }) ||
              findErrorByParam({ param: parameterName, error: inputErrors });
            return (
              <Grow
                key={counter.group_label + counter.service_parameter_id + counter.parameter_name}
                in={!counter?.hidden}
              >
                <div>
                  <Typography fontWeight="bold">{counter.group_label}</Typography>
                  <Fade in={parameters[index]?.hidden} mountOnEnter unmountOnExit>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        aria-label={t("common.delete")}
                        onClick={() => {
                          setCounters((prevState) => {
                            const updatedState = [...prevState];
                            updatedState[index] = { ...prevState[index], hidden: true };
                            updatedState[index + 1] = { ...prevState[index + 1], hidden: true, value: "0" };
                            setValue(updatedState[index + 1].parameter_name, "0");
                            return updatedState;
                          });
                        }}
                        sx={{ stroke: theme.palette.error.main }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Fade>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "0.5fr 0.5fr",
                      alignItems: "baseline",
                      gridColumnGap: theme.spacing(2),
                      marginTop: theme.spacing(2),
                    }}
                  >
                    <CounterInput
                      isFloat={counter.parameter_type === ParameterTypeEnum.FLOAT}
                      isCalculateLoading={false}
                      control={control}
                      registerName={counter.parameter_name}
                      readOnly={counter.read_only}
                      defaultValue={counter.value}
                      label={counter.api_parameter_label}
                      tooltipMessage="payments.counter_from_info"
                    />
                    <CounterInput
                      isFloat={secondParameter.parameter_type === ParameterTypeEnum.FLOAT}
                      isCalculateLoading={false}
                      control={control}
                      registerName={secondParameter.parameter_name}
                      readOnly={secondParameter.read_only}
                      defaultValue={secondParameter.value}
                      label={secondParameter.api_parameter_label}
                      tooltipMessage="payments.counter_to_info"
                    />
                    {rangeError && <InputError sx={{ gridColumn: "1/3" }}>{rangeError}</InputError>}
                  </Box>
                </div>
              </Grow>
            );
          } else {
            return null;
          }
        })}
      {!!counters?.length &&
        counters.map((counter, index) => {
          if (counter.group_label && counter.group_label === counters[index + 1]?.group_label) {
            if (counter.hidden) {
              // show single add button at a time
              if (counter !== counters.filter((x) => x.hidden)[0]) {
                return null;
              }
              return (
                <Fade
                  key={counter.parameter_name}
                  in={counter === counters.filter((x) => x.hidden)[0] && counter.hidden}
                  mountOnEnter
                  unmountOnExit
                >
                  <Button
                    sx={{
                      fontSize: theme.typography.caption.fontSize,
                      padding: theme.spacing(2, 2),
                      justifySelf: "flex-end",
                    }}
                    disableElevation
                    endIcon={
                      <AddOutlined sx={{ borderRadius: "50%", border: `1px solid ${theme.palette.primary.main}` }} />
                    }
                    onClick={() => {
                      setCounters((prevState: Array<IServiceParameter>) => {
                        const updatedState = [...prevState];
                        updatedState[index] = { ...prevState[index], hidden: false };
                        updatedState[index + 1] = { ...prevState[index + 1], hidden: false };
                        return updatedState;
                      });
                    }}
                  >
                    {t("payments.add_counter")}
                  </Button>
                </Fade>
              );
            } else {
              return null;
            }
          } else {
            return null;
          }
        })}
    </>
  );
}
